import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import {
  BookMarked,
  CheckCircle2,
  Clock,
  FileSearch,
  Paperclip,
  Search,
  XCircle,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { AppLayout } from '../../components/layout/app-layout';
import { PageHeader } from '../../components/layout/page-header';
import {
  CardDataTable,
  type CardDataTableColumn,
  type CardDataTableRowDef,
} from '../../components/shared/card-data-table';
import { GradientAvatar } from '../../components/shared/gradient-avatar';
import { StatCard } from '../../components/shared/stat-card';
import { FilterPills } from '../../components/ui/filter-pills';
import { cn } from '../../lib/utils';
import { useAuthStore } from '../../stores/auth.store';

export const Route = createFileRoute('/pledge-studies/')({
  beforeLoad: () => {
    const { user, permissions } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
    if (!permissions.includes('pledge:view')) throw redirect({ to: '/dashboard' });
  },
  component: PledgeStudiesPage,
});

// ── Types ─────────────────────────────────────────────────────────────────────

interface PledgeStudyListItem {
  id: string;
  status: string;
  totalFee: string | null;
  kalbaFee: string | null;
  beeahFee: string | null;
  createdAt: string;
  updatedAt: string;
  pledgeStudyType: { id: string; name: string; code: string };
  request: {
    id: string;
    referenceNo: string;
    status: string;
    requestTypeConfig: { name: string; code: string } | null;
    account: { name: string } | null;
  };
  _count: { attachments: number; measurementPoints: number };
}

// ── API fetch ─────────────────────────────────────────────────────────────────

async function fetchPledgeStudies(): Promise<PledgeStudyListItem[]> {
  const token = useAuthStore.getState().token;
  const res = await fetch('/api/v1/pledge-studies', {
    headers: { Authorization: `Bearer ${token ?? ''}` },
  });
  if (!res.ok) throw new Error('Failed to load pledge studies');
  return res.json();
}

// ── Status config ─────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  DRAFT: {
    label: 'Draft',
    dot: 'bg-amber-400',
    cls: 'border-amber-200 bg-amber-50 text-amber-700',
  },
  APPROVAL_REQUEST: {
    label: 'Approval Request',
    dot: 'bg-purple-400',
    cls: 'border-purple-200 bg-purple-50 text-purple-700',
  },
  APPROVED: {
    label: 'Approved',
    dot: 'bg-success-500',
    cls: 'border-success-200 bg-success-50 text-success-700',
  },
  REJECTED: { label: 'Rejected', dot: 'bg-red-400', cls: 'border-red-200 bg-red-50 text-red-600' },
} as const;

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status as keyof typeof STATUS_CONFIG] ?? STATUS_CONFIG.DRAFT;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold',
        cfg.cls,
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', cfg.dot)} />
      {cfg.label}
    </span>
  );
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function fmtCurrency(val: string | null): string {
  if (!val) return '—';
  const n = Number.parseFloat(val);
  if (Number.isNaN(n)) return '—';
  return n.toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ── Table columns ─────────────────────────────────────────────────────────────

const COLUMNS: CardDataTableColumn[] = [
  { id: 'permit', header: 'Permit Request' },
  { id: 'customer', header: 'Customer' },
  { id: 'type', header: 'Study Type' },
  { id: 'status', header: 'Status' },
  { id: 'fees', header: 'Total Fee', align: 'right' },
  { id: 'files', header: 'Files', align: 'center' },
  { id: 'date', header: 'Date' },
];
const GRID = 'minmax(160px, 1.5fr) minmax(140px, 1.2fr) minmax(120px, 1fr) 150px 110px 70px 100px';

const FILTER_PILLS = [
  { value: '__all__', label: 'All' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'APPROVAL_REQUEST', label: 'Approval Request' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
];

// ── Page ──────────────────────────────────────────────────────────────────────

function PledgeStudiesPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debSearch, setDebSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('__all__');
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setDebSearch(search), 250);
    return () => clearTimeout(timerRef.current);
  }, [search]);

  const {
    data: rawData = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['pledgeStudies'],
    queryFn: fetchPledgeStudies,
  });

  const total = rawData.length;
  const draft = rawData.filter((i) => i.status === 'DRAFT').length;
  const approvalRequest = rawData.filter((i) => i.status === 'APPROVAL_REQUEST').length;
  const approved = rawData.filter((i) => i.status === 'APPROVED').length;

  const filtered = rawData.filter((item) => {
    if (statusFilter !== '__all__' && item.status !== statusFilter) return false;
    if (debSearch) {
      const q = debSearch.toLowerCase();
      return (
        item.request.referenceNo.toLowerCase().includes(q) ||
        (item.request.account?.name ?? '').toLowerCase().includes(q) ||
        item.pledgeStudyType.name.toLowerCase().includes(q) ||
        item.pledgeStudyType.code.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const rows: CardDataTableRowDef[] = filtered.map((item) => ({
    id: item.id,
    avatar: <GradientAvatar name={item.request.referenceNo} size="md" shape="rounded" />,
    cells: {
      permit: (
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-gray-900 group-hover:text-brand-600 transition-colors">
            {item.request.referenceNo}
          </p>
          <div className="mt-1 flex flex-wrap gap-1">
            {item.request.requestTypeConfig && (
              <span className="inline-flex items-center rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-600">
                {item.request.requestTypeConfig.code}
              </span>
            )}
          </div>
        </div>
      ),
      customer: (
        <p className="truncate text-sm text-gray-700">{item.request.account?.name ?? '—'}</p>
      ),
      type: (
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-gray-800">{item.pledgeStudyType.name}</p>
          <p className="truncate text-[10px] text-gray-400 mt-0.5">{item.pledgeStudyType.code}</p>
        </div>
      ),
      status: <StatusBadge status={item.status} />,
      fees: (
        <div className="text-right">
          {item.totalFee ? (
            <p className="text-sm font-semibold text-gray-900">
              <span className="text-[10px] font-normal text-gray-400 mr-0.5">AED</span>
              {fmtCurrency(item.totalFee)}
            </p>
          ) : (
            <p className="text-xs text-gray-400 italic">Pending</p>
          )}
        </div>
      ),
      files: (
        <div className="flex items-center justify-center gap-1 text-sm text-gray-600">
          <Paperclip size={13} className="text-gray-400" />
          <span className="font-medium">{item._count.attachments}</span>
        </div>
      ),
      date: (
        <div className="text-xs text-gray-500">
          <p className="text-[10px] font-medium uppercase text-gray-400 mb-0.5">Created</p>
          <p className="font-medium text-gray-700">{fmtDate(item.createdAt)}</p>
        </div>
      ),
    },
    onClick: () =>
      navigate({ to: '/pledge-studies/$pledgeStudyId', params: { pledgeStudyId: item.id } }),
  }));

  return (
    <AppLayout>
      <PageHeader
        title="Pledge & Study"
        description="All pledge and study records across permit requests"
      />

      {/* Stat cards */}
      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard
          icon={<BookMarked size={18} />}
          iconBg="bg-indigo-50"
          iconColor="text-indigo-500"
          label="Total"
          value={total}
          loading={isLoading}
        />
        <StatCard
          icon={<Clock size={18} />}
          iconBg="bg-amber-50"
          iconColor="text-amber-500"
          label="Draft"
          value={draft}
          loading={isLoading}
        />
        <StatCard
          icon={<FileSearch size={18} />}
          iconBg="bg-purple-50"
          iconColor="text-purple-500"
          label="Approval Request"
          value={approvalRequest}
          loading={isLoading}
        />
        <StatCard
          icon={<CheckCircle2 size={18} />}
          iconBg="bg-success-50"
          iconColor="text-success-500"
          label="Approved"
          value={approved}
          loading={isLoading}
        />
      </div>

      {/* Toolbar */}
      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-gray-200 bg-white p-3 shadow-sm">
        <div className="relative min-w-56 flex-1">
          <Search
            size={14}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by permit, customer, or study type…"
            className="h-9 w-full rounded-xl border border-gray-200 bg-gray-50 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-brand-100 focus:border-brand-400 transition-all placeholder:text-gray-400"
          />
        </div>
        <FilterPills options={FILTER_PILLS} value={statusFilter} onChange={setStatusFilter} />
        <span className="ml-auto text-xs text-gray-400">
          {filtered.length} of {total} record{total !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      <CardDataTable
        gridTemplateColumns={GRID}
        columns={COLUMNS}
        isLoading={isLoading}
        errorContent={
          isError ? (
            <div className="rounded-2xl border border-error-200 bg-error-50 p-8 text-center">
              <p className="text-sm font-medium text-error-600">Failed to load pledge studies</p>
            </div>
          ) : undefined
        }
        emptyContent={
          <div className="rounded-2xl border border-gray-200 bg-white p-16 text-center shadow-sm">
            <XCircle size={40} className="mx-auto mb-4 text-gray-200" />
            <p className="text-sm font-semibold text-gray-500">No pledge studies found</p>
            <p className="mt-1 text-xs text-gray-400">
              {debSearch || statusFilter !== '__all__'
                ? 'Try adjusting your search or filter'
                : 'Pledge & Study records will appear here once permits are submitted for pledge study'}
            </p>
          </div>
        }
        rows={rows}
      />
    </AppLayout>
  );
}
